import { Injectable } from "@angular/core";

import { ApiService } from "src/app/core/services";
import { GeneralService } from "src/app/commons/services";
import { IQuery } from "src/app/core/ITypes";
import { AppConstants } from "../app-constants";

interface IProductCollected {
  amountDifference: number;
  weightDifference: number;
  compensationType: string;
  order_row_id: number;
}
interface IProductComputed {
  salesUnit: string;
  quantity: string;
  price: string;
}

interface IProductBaseTotal {
  product_total: number;
  delivery_charge: number;
  total_amount: number;
  gross_total_amount: number;
  reimbursed_amount?: number;
  paid_at_checkout?: number;
}

interface IWalletAmount {
  wallet_gift_amount: number;
  wallet_store_credit_amount: number;
  wallet_reimbursement_amount: number;
}

interface IAppliedCoupon {
  first: any;
  discountCoupons: any[];
  totalDiscount: number;
  displayCouponCode: string;
  count: number;
}

@Injectable({
  providedIn: "root",
})
export class OrderService {
  reimburseFirstRow: boolean = false;

  constructor(
    private apiService: ApiService,
    private generalService: GeneralService
  ) {}

  async getDetails(orderID, query?: IQuery) {
    this.reimburseFirstRow = false;

    try {
      const { status_code, result } = await this.apiService
        .get(`/order/${orderID}`, query)
        .toPromise();
      if (status_code === 200 && result) {
        return result;
      }
      return {};
    } catch (error) {
      throw error;
    }
  }

  async getDetailsByOrderNumber(orderNo, query?: IQuery) {
    this.reimburseFirstRow = false;

    try {
      const { status_code, result } = await this.apiService
        .get(`/order/order_number/${orderNo}`, query)
        .toPromise();
      if (status_code === 200 && result) {
        return result;
      }
      return {};
    } catch (error) {
      throw error;
    }
  }

  isDeliveryReimbursed(order: any, orderType: string) {
    if (orderType === AppConstants.COMPENSATION_TYPE_RECLAMATION) {
      return order.products.some((item) => {
        const meta = JSON.parse(item.meta);
        if (meta) {
          return meta.is_delivery_reimbursed === true;
        }
      });
    } else {
      return true;
    }
  }

  renderProductTitle(product) {
    let isReimburse: boolean = product.difference ? true : false;

    if (product.detail) {
      const defaultVal = product.detail.public.default;

      let productName = defaultVal.F001 || "";

      if (defaultVal.F002) {
        if (productName) {
          productName += `, ${defaultVal.F002}`;
        } else {
          productName = defaultVal.F002;
        }
      }

      if (defaultVal.C002 == "PACK" || (defaultVal.C007 && !isReimburse)) {
        productName += `, ${defaultVal.F005}`;
      }

      return productName;
    }
    return "";
  }

  renderSalesUnit(product: any) {
    const price: number =
      product.discount_price === null ? product.price : product.discount_price;
    let salesUnit = `${this.generalService.parseAmount(price, 2)} €`;
    const difference: IProductCollected =
      product.difference && JSON.parse(product.difference);

    if (
      difference !== null &&
      (difference.weightDifference || difference.amountDifference < 1)
    ) {
      const weightPrice =
        product.display_price !== null
          ? product.display_price
          : product.detail.public.default.F018;
      salesUnit = `${this.generalService.parseAmount(weightPrice, 2)} € / kg`;
    }

    return salesUnit;
  }

  renderQuantity(product) {
    let qty = product.quantity;
    const difference = product.difference && JSON.parse(product.difference);

    // If amountDifference then its a variable weight product but unit based not weight bbased
    // Reason wight has been converted into unit on reimbursement
    if (
      difference !== null &&
      (difference.weightDifference || difference.amountDifference < 1)
    ) {
      let diffInKg: any = 0;
      if (difference.weightDifference) {
        diffInKg = Math.abs(difference.weightDifference);
      } else if (difference.amountDifference < 1) {
        const weight =
          difference.amountDifference *
          product.amount_of_original_order_row *
          product.product_detail.public.default.F019;
        diffInKg = Math.abs(weight);
      }

      if (diffInKg < 1) {
        //convert kg to grams and remove decimal
        const parseDiffInKg = diffInKg.toFixed(2);
        diffInKg = Math.floor(parseDiffInKg * 1000);
        qty = `-${this.generalService.parseQty(diffInKg, 0)}g`;
      } else {
        qty = `-${this.generalService.parseQty(diffInKg, 3)}kg`;
      }
    }

    return qty;
  }

  calculateProductPrice(product: any) {
    const price: number =
      product.discount_price === null ? product.price : product.discount_price;

    return (price * product.quantity).toFixed(2);
  }

  calculateProductRow(product) {
    let computed = <IProductComputed>{
      salesUnit: this.renderSalesUnit(product),
      quantity: this.renderQuantity(product),
      price: this.calculateProductPrice(product),
    };

    return computed;
  }

  calculateProductTotal(order: any, orderType: string) {
    let productTotal: number = 0;

    if (orderType === AppConstants.COMPENSATION_TYPE_RECLAMATION) {
      productTotal = order.products.reduce(
        (pv, cv) => pv + parseFloat(this.calculateProductPrice(cv)),
        0
      );
    } else {
      if (order.invoiced_product_total !== null) {
        productTotal = order.invoiced_product_total;
      } else if (order.meta.original_amount) {
        productTotal = order.meta.original_amount.original_product_total;
      } else {
        productTotal = order.product_total;
      }
    }

    return productTotal;
  }

  calculateDeliveryCharge(order: any, orderType: string) {
    let deliveryCharge: number = 0;

    if (order.invoiced_delivery_charge !== null) {
      deliveryCharge = order.invoiced_delivery_charge;
    } else if (order.meta.original_amount) {
      deliveryCharge = order.meta.original_amount.original_delivery_fee;
    } else {
      deliveryCharge = order.delivery_charge;
    }

    return orderType === AppConstants.COMPENSATION_TYPE_RECLAMATION
      ? -deliveryCharge
      : deliveryCharge;
  }

  calculateProductTotalWithDeliveryCharge(
    order: any,
    orderType: string,
    excludeDiscount = false
  ) {
    let total: number = 0;
    if (orderType === AppConstants.COMPENSATION_TYPE_RECLAMATION) {
      if (this.isDeliveryReimbursed(order, orderType)) {
        total =
          this.calculateProductTotal(order, orderType) +
          this.calculateDeliveryCharge(order, orderType);
      } else {
        total = this.calculateProductTotal(order, orderType);
      }
    } else {
      if (order.invoiced_order_total !== null) {
        total = order.invoiced_order_total;
      } else if (order.meta.original_amount) {
        total = order.meta.original_amount.original_order_total;
      } else {
        total = order.product_total + order.delivery_charge;
      }

      if (!excludeDiscount) {
        total = total - order.discount_amount;
      }
    }

    return total;
  }

  calculateTaxes(order: any, orderType: string) {
    let calTax = [];
    let totalTax: number = 0;

    //Unique taxes
    order.products.forEach((product) => {
      const price =
        product.discount_price === null
          ? product.price
          : product.discount_price;
      const tax_percent = product.tax;

      const foundTaxPercentIndex = calTax.findIndex(
        (item) => item.tax_percent === tax_percent
      );

      //If not found then add otherwise update
      if (foundTaxPercentIndex !== -1) {
        calTax[foundTaxPercentIndex].tax_amount += price * product.quantity;
      } else {
        let tax_delivery = 0;
        if (
          [
            AppConstants.COMPENSATION_TYPE_RECLAMATION,
            AppConstants.COMPENSATION_TYPE_CANCELLATION,
          ].includes(orderType)
        ) {
          tax_delivery = this.isDeliveryReimbursed(order, orderType)
            ? this.calculateDeliveryCharge(order, orderType)
            : 0;
        } else {
          tax_delivery =
            order.tax === tax_percent
              ? this.calculateDeliveryCharge(order, orderType)
              : 0;
        }
        const tax_amount = price * product.quantity + tax_delivery;
        calTax.push({ tax_percent, tax_amount });
      }
    });

    //Unique taxes amount
    calTax.forEach((element) => {
      const taxSlab: number =
        element.tax_amount -
        element.tax_amount / ((100 + element.tax_percent) / 100);

      element.tax_amount = taxSlab;
      totalTax += taxSlab;
    });

    //Sort By Descending
    calTax.sort((a, b) => b.tax_percent - a.tax_percent);

    return calTax;
  }

  calculateWalletAmount(order: any) {
    const {
      wallet_gift_amount,
      wallet_store_credit_amount,
      wallet_reimbursement_amount,
    } = <IWalletAmount>order;

    return (
      wallet_gift_amount +
      wallet_store_credit_amount +
      wallet_reimbursement_amount
    );
  }

  calculateBaseTotal(order: any, orderType: string) {
    const product_total: number = this.calculateProductTotal(order, orderType);
    const delivery_charge: number = this.calculateDeliveryCharge(
      order,
      orderType
    );
    const total_amount: number = this.calculateProductTotalWithDeliveryCharge(
      order,
      orderType
    );
    const gross_total_amount: number =
      this.calculateProductTotalWithDeliveryCharge(order, orderType, true);
    const reimbursed_amount = this.calculateReimbursedAmount(order, orderType);

    let computed = <IProductBaseTotal>{
      product_total,
      delivery_charge,
      total_amount,
      gross_total_amount,
      reimbursed_amount,
      paid_at_checkout: gross_total_amount + reimbursed_amount,
    };

    return computed;
  }

  getAppliedCouponDetails(order): IAppliedCoupon {
    const coupon = {
      first: null,
      discountCoupons: [],
      totalDiscount: 0,
      displayCouponCode: "",
      count: 0,
    };
    let filteredCoupons = [];

    const discountsList = JSON.parse(order.multi_discount);

    if (discountsList?.length && discountsList[0].discount_type == "fixed") {
      filteredCoupons = discountsList.filter(
        (item) => item.discount_type == "fixed"
      );
      coupon.discountCoupons = filteredCoupons;
      coupon.count = filteredCoupons.length;
    }

    if (filteredCoupons.length) {
      coupon.first = coupon.discountCoupons[0];
      coupon.totalDiscount = order.discount_amount;
      coupon.displayCouponCode =
        filteredCoupons.length == 1
          ? coupon.discountCoupons[0].discount_code
          : "";
    }

    return coupon;
  }

  calculateReimbursedAmount(order, orderType = null) {
    const reimbursedProducts = order.products
      .filter((product) => (product.difference ? true : false))
      .map((product) => {
        product.returnPrice = this.calculateProductPrice(product);
        return product;
      });

    let reimbursedAmount = 0;
    let orderMeta = JSON.parse(order.meta);

    if (
      orderType == AppConstants.COMPENSATION_TYPE_REIMBURSEMENT ||
      reimbursedProducts.length
    ) {
      const totalProductsReturnPrice = reimbursedProducts.reduce(
        (p, item) => p + parseFloat(item.returnPrice),
        0
      );

      if (order.invoiced_product_total !== null) {
        reimbursedAmount =
          order.proforma_order_total - order.invoiced_order_total;
      } else if (orderMeta && order.meta.original_amount) {
        // reimbursedAmount = order.meta.original_amount.original_product_total - totalProductsReturnPrice;
        reimbursedAmount = totalProductsReturnPrice;
      } else {
        reimbursedAmount = totalProductsReturnPrice;
      }
    }

    return Math.abs(reimbursedAmount);
  }
}
